/**
 * Copyright (c) 2026 Accunai India Services Pvt. Ltd. All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This file is part of proprietary software owned by Accunai India Services Pvt. Ltd.
 * Unauthorized copying, distribution, modification, or use of this file,
 * in whole or in part, via any medium, is strictly prohibited without
 * prior written permission from Accunai India Services Pvt. Ltd.
 *
 * Generated with GitHub Copilot Business/Enterprise.
 * Reviewed and approved by: AI-assisted / 2026-08-13
 */

import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { constants } from "../constants/env";
import { httpClient } from "./httpClient";
import { clearAuthCookies, getAuthTokenCookie } from "../shared/auth/cookieStore";
import { normalizeApiError } from "../shared/errors/normalizeApiError";

export const getAuthTokenFromLocalStorage = () => localStorage.getItem("authToken") || null;

const LOGOUT_ENDPOINT = "auth/company/user/logout/other/";
const LOGIN_REDIRECT_PATH = "/new/login";
const DEFAULT_TIMEOUT_MS = 450000;

const buildApiUrl = (path: string): string => {
  const baseUrl = constants.API_BASE_URL.endsWith("/")
    ? constants.API_BASE_URL
    : `${constants.API_BASE_URL}/`;

  return new URL(path, baseUrl).toString();
};

const clearClientSession = (): void => {
  clearAuthCookies();
  localStorage.removeItem("authenticated");
};

const redirectToLogin = (): void => {
  if (window.location.pathname !== LOGIN_REDIRECT_PATH) {
    window.location.href = LOGIN_REDIRECT_PATH;
  }
};

const isAbsoluteUrl = (url: string): boolean => /^https?:\/\//i.test(url);

const resolveUrl = (url: string): string => {
  if (!url) {
    return buildApiUrl("");
  }

  if (isAbsoluteUrl(url)) {
    return url;
  }

  return buildApiUrl(url.startsWith("/") ? url.slice(1) : url);
};

const request = <T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  const resolvedConfig: AxiosRequestConfig = {
    ...config,
    url: resolveUrl(config.url || ""),
    timeout: config.timeout ?? DEFAULT_TIMEOUT_MS,
  };

  if (typeof FormData !== "undefined" && resolvedConfig.data instanceof FormData) {
    const requestHeaders: Record<string, string | undefined> = {
      ...((resolvedConfig.headers as Record<string, string | undefined>) || {}),
    };
    delete requestHeaders["Content-Type"];
    delete requestHeaders["content-type"];
    resolvedConfig.headers = requestHeaders;
  }

  return httpClient.request<T>(resolvedConfig);
};

const api = {
  get: <T = any>(url: string, config: AxiosRequestConfig = {}) =>
    request<T>({ ...config, method: "get", url }),
  post: <T = any>(url: string, data?: any, config: AxiosRequestConfig = {}) =>
    request<T>({ ...config, method: "post", url, data }),
  patch: <T = any>(url: string, data?: any, config: AxiosRequestConfig = {}) =>
    request<T>({ ...config, method: "patch", url, data }),
  put: <T = any>(url: string, data?: any, config: AxiosRequestConfig = {}) =>
    request<T>({ ...config, method: "put", url, data }),
  delete: <T = any>(url: string, config: AxiosRequestConfig = {}) =>
    request<T>({ ...config, method: "delete", url }),
};

export const logoutCurrentSession = async (): Promise<void> => {
  const authToken = getAuthTokenCookie();

  clearClientSession();

  if (authToken) {
    try {
      await request({
        method: "post",
        url: LOGOUT_ENDPOINT,
        data: {},
        headers: { Authorization: `Bearer ${authToken}` },
        ...( { _skipAuthRefresh: true } as any ),
      });
    } catch (_error) {
      // Session is already cleared on client side.
    }
  }

  redirectToLogin();
};

export const headers = {
  "Cache-Control": "no-cache",
  "Content-Type": "application/json",
};

export const createRoute = async (requestData: any): Promise<any> => {
  try {
    const response = await request({
      method: "post",
      url: "/route/",
      data: requestData,
      headers: { ...headers },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
};

export const fetchConfigOptions = async (configId: string): Promise<any> => {
  try {
    const response = await request({
      method: "get",
      url: `/project/common/config_options/?config_id=${configId}`,
      headers: { ...headers },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
};

export const downloadFileFromUrl = async (
  url: string,
  fallbackFileName = "downloaded-file"
): Promise<void> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Failed to download file");
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = url.split("/").pop()?.split("?")[0] || fallbackFileName;

  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export const handleAxiosError = (error: any) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as { response?: { data?: { message?: string } }; request?: unknown };
    console.error("Axios Error:", error);
    if (axiosError.response) {
      console.error("Response Data:", axiosError.response.data);
      const normalizedError = normalizeApiError(
        error,
        "Failed to process the request",
        "Session expired. Please login again."
      );
      throw new Error(normalizedError.message);
    } else if (axiosError.request) {
      console.error("No response received from server");
      throw new Error("No response received from server");
    } else {
      console.error("Error setting up the request");
      throw new Error("Error setting up the request");
    }
  } else {
    console.error("Unexpected Error:", error);
    throw new Error("An unexpected error occurred");
  }
};

export default api;
